import React, { useState } from 'react';
import { BackButton } from '../BackButton/BackButton';
import { TranslationUtils } from '../../utils';
import { useSelector } from 'react-redux';
import { StoreState } from '../../types/store.type';
import { RequiredActions, Reservation } from 'guest-data-types-ts-lib';
import { Loading } from '../Loading/Loading';
import './NewRentalAgreement.scss';
import { getHouseRuleIconByKey } from './RentalAgreementHelper';
import { IconAlertCircle, IconCheckSquare, IconHouseTerms, IconX } from '../../assets/icons';
import { Button } from '../Button/Button';
import { Modal } from 'react-bootstrap';
import { FormattedDate } from 'react-intl';
import * as _ from 'lodash';

interface NewRentalAgreementProps {
    isSubmitDisabled: boolean;
    isSubmitLoading: boolean;
    isCheckPoliciesDisabled: boolean;
    checkPolicies: boolean;
    handleSubmit: () => Promise<void>;
    handleChangeAgreement: (e: any) => void;
}

export const NewRentalAgreement: React.FC<NewRentalAgreementProps> = (props) => {
    const [showUnitTerms, setShowUnitTerms] = useState(false);

    const trip = useSelector((store: StoreState) => store.trip);

    const { isSubmitDisabled, isSubmitLoading, isCheckPoliciesDisabled, checkPolicies, handleSubmit, handleChangeAgreement } = props;

    if (trip.isLoading) {
        return <Loading />;
    }
    let requiredActions: RequiredActions | undefined;
    requiredActions = trip.required_actions;

    if (!requiredActions) {
        return null;
    }

    const { contract_agreement: contractAgreement } = requiredActions;

    const newTermsData = contractAgreement?.new_terms;
    const houseRules = newTermsData?.house_rules;
    if (!newTermsData || !houseRules) {
        return null;
    }

    const unitTerms = newTermsData?.unit_specific_terms || '';
    const urlRentalTerms = newTermsData?.rental_agreement_url || '';
    const urlPrivacyPolice = newTermsData?.privacy_policy_url || '';

    const isAgreementCompleted = contractAgreement?.completed && contractAgreement.completed_date;

    if (!houseRules) {
        return null;
    }

    const UnitTermsBtn = () => (
        <span onClick={() => setShowUnitTerms(true)} className={'unit-terms-btn'}>
            {TranslationUtils.get('unit_terms_text')}
        </span>
    );
    const formatCompletedDate = (completedDate: string) => {
        return <FormattedDate value={new Date(completedDate)} day="numeric" month="short" year="numeric" />;
    };

    return (
        <div>
            <BackButton path="/reservation" />
            <h1 tabIndex={0} className="page-title no-print">
                {TranslationUtils.get('rental_agreement')}
            </h1>

            <div id={'rental-instructions'} className={'rental-instructions'}>
                {TranslationUtils.get('new_terms_instructions_text')}
            </div>
            <div id={'house-rules-container'} className={'house-rules-container'}>
                <div className={'house-rules-list'}>
                    {houseRules.map((rule: any) => (
                        <div className={'house-rules-list-el'}>
                            <span className={'rule-icon'}>{getHouseRuleIconByKey(rule.icon)}</span>
                            <span className={'rule-text body-regular'}>{rule.text}</span>
                        </div>
                    ))}
                </div>
                <hr />
                <div className={'house-rules-unit'}>
                    <div className={'rule-unit-container'}>
                        <span className={'house-rules-unit-icon'}>
                            <IconAlertCircle />
                        </span>
                        <span>
                            <span className={'house-rules-unit-text'}>{TranslationUtils.get('unit_terms_alert')}</span>

                            <UnitTermsBtn />
                        </span>
                    </div>
                </div>
            </div>
            {isAgreementCompleted ? (
                <div id={'rental-confirmation-container'} className={'rental-confirmation'}>
                    <div className={'rental-conf-checkbox'}>
                        <div className={'check-input'}>
                            <IconCheckSquare />
                        </div>
                        <span id={'check-conf-text'}>
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: TranslationUtils.get('agreed_new_terms_on', {
                                        rental_agreement_url: urlRentalTerms,
                                        privacy_policy_url: urlPrivacyPolice,
                                    }),
                                }}
                            ></span>
                            <UnitTermsBtn />
                            <span>
                                {TranslationUtils.get('agreed_new_terms_on_date', {
                                    date: formatCompletedDate(contractAgreement?.completed_date || ''),
                                })}
                            </span>
                        </span>
                    </div>
                </div>
            ) : (
                <div>
                    <div id={'rental-confirmation-container'} className={'rental-confirmation'}>
                        <div className={'rental-conf-checkbox'}>
                            <div className={'check-input'}>
                                <span className={'check'}></span>
                            </div>
                            <span className={'required-text'}>{TranslationUtils.get('new_terms_required_text')}</span>
                        </div>
                        <div className={'rental-conf-checkbox'}>
                            <div className={'check-input'}>
                                <input
                                    className={'check'}
                                    type="checkbox"
                                    checked={checkPolicies}
                                    onChange={handleChangeAgreement}
                                    disabled={isCheckPoliciesDisabled}
                                />
                            </div>
                            <span id={'check-conf-text'}>
                                <span
                                    className={'check-text'}
                                    dangerouslySetInnerHTML={{
                                        __html: TranslationUtils.get('i_agree_new_terms', {
                                            rental_agreement_url: urlRentalTerms,
                                            privacy_policy_url: urlPrivacyPolice,
                                        }),
                                    }}
                                ></span>
                                <UnitTermsBtn />
                            </span>
                        </div>
                    </div>

                    <div id={'submit-new-terms'} className={'new-terms-btn'}>
                        <div className="rental-buttons">
                            <Button
                                label={TranslationUtils.get('new_terms_submit_btn')}
                                variant="primary"
                                className=""
                                onClick={handleSubmit}
                                disabled={isSubmitDisabled}
                                isLoading={isSubmitLoading}
                            />
                        </div>
                    </div>
                </div>
            )}

            {showUnitTerms && (
                <Modal
                    centered={true}
                    id={'unit-terms-modal'}
                    backdropClassName={'unit-terms-modal'}
                    show={showUnitTerms}
                    onHide={() => setShowUnitTerms(false)}
                >
                    <Modal.Header>
                        <div className={'unit-terms-header'}>
                            <div className={'unit-terms-title'}>
                                <IconHouseTerms />
                                <span className={'modal-title heading-small'}>{TranslationUtils.get('unit_terms_text')}</span>
                            </div>
                            <div onClick={() => setShowUnitTerms(false)} className={'unit-terms-close'}>
                                <IconX />
                            </div>
                        </div>
                    </Modal.Header>
                    <Modal.Body>
                        <div
                            className={'unit-terms-data'}
                            dangerouslySetInnerHTML={{
                                __html: unitTerms,
                            }}
                        />
                    </Modal.Body>
                </Modal>
            )}
        </div>
    );
};
